import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Phone, Mail, MapPin, Clock, Send, CheckCircle } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const Contact = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    projectType: '',
    surface: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${API_URL}/api/contact/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (response.ok) {
        toast({
          title: "Demande envoyée avec succès !",
          description: data.message || "Nous vous contacterons dans les plus brefs délais.",
        });
        
        // Réinitialiser le formulaire
        setFormData({
          name: '',
          email: '',
          phone: '',
          projectType: '',
          surface: '',
          message: ''
        });
      } else {
        throw new Error(data.detail || "Erreur lors de l'envoi");
      }
    } catch (error) {
      console.error('Erreur lors de l\'envoi du formulaire:', error);
      toast({
        title: "Erreur",
        description: "Une erreur s'est produite. Veuillez réessayer ou nous contacter directement.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-20 bg-gradient-to-b from-gray-50 to-white">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-block bg-[#c9a961]/10 text-[#c9a961] px-4 py-2 rounded-full text-sm font-semibold mb-4">
            Contact
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-[#0B1F3C] mb-6">
            Parlons de votre projet
          </h2>
          <p className="text-lg text-gray-600">
            Obtenez votre devis gratuit et sans engagement. Notre équipe vous répond sous 24h.
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
          {/* Contact Form */}
          <div className="lg:col-span-2">
            <Card className="border-0 shadow-xl">
              <CardHeader className="bg-gradient-to-r from-[#0B1F3C] to-[#2a4a62] text-white rounded-t-lg">
                <CardTitle className="text-2xl">Demande de devis gratuit</CardTitle>
                <p className="text-white/90 text-sm mt-2">
                  Remplissez ce formulaire et nous vous recontacterons rapidement
                </p>
              </CardHeader>
              <CardContent className="p-8">
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="name" className="text-[#0B1F3C] font-medium">
                        Nom complet *
                      </Label>
                      <Input
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        placeholder="Jean Dupont"
                        className="border-gray-300 focus:border-[#c9a961] focus:ring-[#c9a961]"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email" className="text-[#0B1F3C] font-medium">
                        Email *
                      </Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        placeholder="jean.dupont@email.com"
                        className="border-gray-300 focus:border-[#c9a961] focus:ring-[#c9a961]"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="phone" className="text-[#0B1F3C] font-medium">
                        Téléphone *
                      </Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleChange}
                        required
                        placeholder="06 XX XX XX XX"
                        className="border-gray-300 focus:border-[#c9a961] focus:ring-[#c9a961]"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="projectType" className="text-[#0B1F3C] font-medium">
                        Type de projet
                      </Label>
                      <select
                        id="projectType"
                        name="projectType"
                        value={formData.projectType}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#c9a961] focus:border-[#c9a961]"
                      >
                        <option value="">Sélectionnez</option>
                        <option value="renovation-complete">Rénovation Complète</option>
                        <option value="plomberie">Plomberie & Sanitaires</option>
                        <option value="electricite">Électricité</option>
                        <option value="peinture">Peinture & Décoration</option>
                        <option value="sols">Sols & Revêtements</option>
                        <option value="menuiserie">Menuiserie</option>
                      </select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="surface" className="text-[#0B1F3C] font-medium">
                      Surface approximative (m²)
                    </Label>
                    <Input
                      id="surface"
                      name="surface"
                      value={formData.surface}
                      onChange={handleChange}
                      placeholder="Ex: 80 m²"
                      className="border-gray-300 focus:border-[#c9a961] focus:ring-[#c9a961]"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="message" className="text-[#0B1F3C] font-medium">
                      Décrivez votre projet *
                    </Label>
                    <Textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={5}
                      placeholder="Parlez-nous de votre projet de rénovation..."
                      className="border-gray-300 focus:border-[#c9a961] focus:ring-[#c9a961]"
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-[#c9a961] hover:bg-[#b89951] text-white font-semibold py-6 text-lg"
                  >
                    {isSubmitting ? (
                      'Envoi en cours...'
                    ) : (
                      <>
                        Envoyer ma demande
                        <Send className="ml-2" size={20} />
                      </>
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>

          {/* Contact Info */}
          <div className="space-y-6">
            {/* Contact Details Card */}
            <Card className="border-0 shadow-xl">
              <CardContent className="p-8 space-y-6">
                <div>
                  <h3 className="text-xl font-bold text-[#0B1F3C] mb-6">Informations de contact</h3>
                  
                  <div className="space-y-5">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-[#c9a961]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Phone className="text-[#c9a961]" size={20} />
                      </div>
                      <div>
                        <div className="font-semibold text-[#0B1F3C] mb-1">Téléphone</div>
                        <a href="tel:0666770127" className="text-gray-600 hover:text-[#c9a961] transition-colors">
                          06 66 77 01 27
                        </a>
                      </div>
                    </div>

                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-[#c9a961]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Mail className="text-[#c9a961]" size={20} />
                      </div>
                      <div>
                        <div className="font-semibold text-[#0B1F3C] mb-1">Email</div>
                        <a href="mailto:concept.domus.co@gmail.com" className="text-gray-600 hover:text-[#c9a961] transition-colors">
                          concept.domus.co@gmail.com
                        </a>
                      </div>
                    </div>

                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-[#c9a961]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                        <MapPin className="text-[#c9a961]" size={20} />
                      </div>
                      <div>
                        <div className="font-semibold text-[#0B1F3C] mb-1">Zone d'intervention</div>
                        <p className="text-gray-600">Toute l'Île-de-France</p>
                      </div>
                    </div>

                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-[#c9a961]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Clock className="text-[#c9a961]" size={20} />
                      </div>
                      <div>
                        <div className="font-semibold text-[#0B1F3C] mb-1">Horaires</div>
                        <p className="text-gray-600">Lun - Ven: 8h - 18h</p>
                        <p className="text-gray-600">Sam: 9h - 13h</p>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Why Choose Us Card */}
            <Card className="border-0 shadow-xl bg-gradient-to-br from-[#0B1F3C] to-[#2a4a62] text-white">
              <CardContent className="p-8">
                <h3 className="text-xl font-bold mb-4">Pourquoi nous choisir ?</h3>
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <CheckCircle className="text-[#c9a961] flex-shrink-0 mt-1" size={20} />
                    <span className="text-white/90">Devis gratuit sous 24h</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="text-[#c9a961] flex-shrink-0 mt-1" size={20} />
                    <span className="text-white/90">Artisans qualifiés RGE</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="text-[#c9a961] flex-shrink-0 mt-1" size={20} />
                    <span className="text-white/90">Garantie décennale</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="text-[#c9a961] flex-shrink-0 mt-1" size={20} />
                    <span className="text-white/90">Respect des délais</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
